import React from "react";
import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import { RelatorioStyles as styles } from "./styles";
import { RelatorioComparacaoValores } from "../../../shared/dtos/DadosFinanceiros.dto";
import { formatDateToBR } from "../../../utils/dateUtils/dateUtils";

interface RelatorioPDFProps {
  data: RelatorioComparacaoValores[];
  lotacao?: string;
  localPagamento?: string;
  dateRange: {
    inicio: string;
    fim: string;
  };
}

const ITEMS_PER_PAGE = 7;

const RelatorioPDF: React.FC<RelatorioPDFProps> = ({
  data,
  lotacao,
  localPagamento,
  dateRange,
}) => {
  // Formatar valor monetário
  const formatCurrency = (value: number | string): string => {
    if (typeof value === "string") {
      if (value.includes("R$")) return value;
      // Remover possíveis formatações que possam estar no valor string
      const valorLimpo = value.replace(/[^\d,.]/g, "").replace(",", ".");
      const valorNumerico = parseFloat(valorLimpo);

      if (isNaN(valorNumerico)) {
        return `R$ ${value}`;
      }

      return `R$ ${valorNumerico.toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }

    return `R$ ${value.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Dividir os dados em páginas
  const pages = [];
  for (let i = 0; i < data.length; i += ITEMS_PER_PAGE) {
    pages.push(data.slice(i, i + ITEMS_PER_PAGE));
  }

  return (
    <Document>
      {pages.map((pageData, pageIndex) => (
        <Page
          key={pageIndex}
          style={styles.page}
          size="A4"
          orientation="landscape"
        >
          {/* Cabeçalho */}
          <View style={styles.header}>
            <View style={styles.logoContainer}>
              <Image
                src={require("../../../assets/logo-imagem.png")}
                style={styles.logo}
              />
            </View>
            <View style={styles.headerText}>
              <Text style={styles.institution}>
                Associação Beneficente Nossa Senhora da Conceição
              </Text>
              <Text style={styles.details}>
                Santa Casa de Misericórdia e Maternidade Nossa Senhora da
                Conceição
              </Text>
              <Text style={styles.details}>
                CNPJ 47.531.835/0001-31 Inscrição Estadual: isenta
              </Text>
              <Text style={styles.details}>
                Avenida Padre Rodolfo, 320 - Alto do Cruzeiro - 12530-000 -
                Cunha-SP
              </Text>
              <Text style={styles.title}>
                RELATÓRIO DE COMPARAÇÃO VALORES DOS DADOS FINANCEIROS
              </Text>
            </View>
          </View>

          {/* Tabela */}
          <View style={styles.table}>
            <View style={styles.row}>
              <Text style={styles.cellHeaderName}>Nome Contribuinte</Text>
              <Text style={styles.cellHeader}>Data Inclusão</Text>
              <Text style={styles.cellHeader}>Dt.Incl.Dados Financeiros</Text>
              <Text style={styles.cellHeader}>Dt.Excl. Dados Financeiros</Text>
              <Text style={styles.cellHeader}>Lotação</Text>
              <Text style={styles.cellHeader}>Valor titular</Text>
              <Text style={styles.cellHeader}>Valor dependente</Text>
            </View>

            {pageData.map((item, index) => (
              <React.Fragment key={`${pageIndex}-${index}`}>
                <View style={styles.row}>
                  <Text style={styles.cellPreviousName}>
                    {item.previous.contribuinte?.nome.toUpperCase()}
                  </Text>
                  <Text style={styles.cellPrevious}>
                    {item.previous.contribuinte?.movimentacoes?.[0]
                      ?.data_movimentacao
                      ? formatDateToBR(
                          item.previous.contribuinte.movimentacoes[0]
                            .data_movimentacao
                        )
                      : "-"}
                  </Text>
                  <Text style={styles.cellPrevious}>
                    {item.previous.dataInclusao
                      ? formatDateToBR(item.previous.dataInclusao)
                      : "-"}
                  </Text>
                  <Text style={styles.cellPrevious}>
                    {item.previous.dataExclusao
                      ? formatDateToBR(item.previous.dataExclusao)
                      : "-"}
                  </Text>
                  <Text style={styles.cellPrevious}>
                    {item.previous.lotacao?.descricao || "-"}
                  </Text>
                  <Text style={styles.cellPrevious}>
                    {item.previous.valorTitular
                      ? formatCurrency(item.previous.valorTitular)
                      : "-"}
                  </Text>
                  <Text style={styles.cellPrevious}>
                    {item.previous.valorDependente
                      ? formatCurrency(item.previous.valorDependente)
                      : "R$ 0,00"}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.cellCurrentName}>
                    {item.current.contribuinte?.nome.toUpperCase()}
                  </Text>
                  <Text style={styles.cellCurrent}>
                    {item.current.contribuinte?.movimentacoes?.[0]
                      ?.data_movimentacao
                      ? formatDateToBR(
                          item.current.contribuinte.movimentacoes[0]
                            .data_movimentacao
                        )
                      : "-"}
                  </Text>
                  <Text style={styles.cellCurrent}>
                    {item.current.dataInclusao
                      ? formatDateToBR(item.current.dataInclusao)
                      : "-"}
                  </Text>
                  <Text style={styles.cellCurrent}>
                    {item.current.dataExclusao
                      ? formatDateToBR(item.current.dataExclusao)
                      : "-"}
                  </Text>
                  <Text style={styles.cellCurrent}>
                    {item.current.lotacao?.descricao || "-"}
                  </Text>
                  <Text style={styles.cellCurrent}>
                    {item.current.valorTitular
                      ? formatCurrency(item.current.valorTitular)
                      : "-"}
                  </Text>
                  <Text style={styles.cellCurrent}>
                    {item.current.valorDependente
                      ? formatCurrency(item.current.valorDependente)
                      : "R$ 0,00"}
                  </Text>
                </View>
                <View style={styles.row}>
                  <Text style={styles.cellFull}></Text>
                </View>
              </React.Fragment>
            ))}
          </View>

          {/* Total apenas na última página */}
          {pageIndex === pages.length - 1 && (
            <View style={styles.filtersMainContainer}>
              <View style={styles.filters}>
                <View style={styles.filterContainer}>
                  <Text style={styles.filterText}>Lotação: </Text>
                  <Text style={styles.filterTextBold}>
                    {lotacao || "Todas"}
                  </Text>
                </View>
                <View style={styles.filterContainer}>
                  <Text style={styles.filterText}>Local de Pagamento: </Text>
                  <Text style={styles.filterTextBold}>
                    {localPagamento || "Todas"}
                  </Text>
                </View>
                <View style={styles.filterContainer}>
                  <Text style={styles.filterText}>Período: </Text>
                  <Text style={styles.filterTextBold}>
                    {formatDateToBR(dateRange.inicio)} até{" "}
                    {formatDateToBR(dateRange.fim)}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {/* Rodapé */}
          <View style={styles.footer}>
            <Text>
              Data de emissão: {new Date().toLocaleDateString("pt-BR")}
            </Text>
            <Text
              render={({ pageNumber, totalPages }) =>
                `Página: ${pageNumber} de ${totalPages}`
              }
            />
            <Text>Desenvolvido por FATECOINS</Text>
          </View>
        </Page>
      ))}
    </Document>
  );
};

export default RelatorioPDF;
